import {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Papa from 'papaparse';
import {format, formatDistanceToNow} from 'date-fns';
import {
  Check,
  X,
  RotateCw,
  Pencil,
  OctagonAlert,
  Search,
  Download,
  MoreHorizontal
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
  CopyButton
} from '@kroll-resolver-crisp/ui-components-react';
import {
  EscalationVariantPicker,
  getPolicyViolationsMissingVariant
} from './EscalationVariantPicker';
import {P0AlertDetailSheet} from './P0AlertDetailSheet';
import {statusBadge} from './alertStatus';
import {
  useInfiniteP0Alerts,
  useSubmissionStats,
  useP0Alert,
  useDecideP0Alert,
  useAmendP0Alert,
  useEscalationVariants
} from '@/api/hooks';
import {type P0Alert, type P0AlertStatus} from '@/api/p0Alerts';
import {getApiErrorMessage} from '@/api/client';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {
  RichTextArea,
  isRichTextEmpty,
  richTextToPlainLines,
  EmailPreview
} from '@/components/shared/RichTextArea';
import {cn} from '@/lib/utils';
import {showToast} from '@/lib/toast';
import {ACTOR_LABEL, CONTENT_LABEL} from '@/lib/platform';
import {StatusIcon} from '@/components/shared/StatusIcon';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';
import {FilterChip} from '@/components/shared/FilterChip';
import {PageHeaderActions} from '@/components/layout/PageHeader';
import {enforcementActions} from '@/lib/enforcement';
import {DateRangeFilter} from '@/components/submissions/SubmissionFilters';
import {useDebounce} from '@/lib/useDebounce';
import {TruncatedText} from '@/components/shared/TruncatedText';

const STATUS_TABS: Array<{value: P0AlertStatus | 'all'; label: string}> = [
  {value: 'all', label: 'All'},
  {value: 'pending_approval', label: 'Pending'},
  {value: 'approved', label: 'Delivery Failed'},
  {value: 'delivered', label: 'Delivered'},
  {value: 'rejected', label: 'Rejected'}
];

const VISIBLE_RECIPIENTS = 2;

/** First few recipients plus a count, so a long list can't widen the Subject column. */
function RecipientSummary({
  label,
  recipients
}: {
  readonly label: string;
  readonly recipients: readonly string[];
}) {
  const hidden = recipients.length - VISIBLE_RECIPIENTS;
  // A popover rather than a title tooltip: a tooltip vanishes as the pointer moves toward it, so
  // its text could never be selected. React events bubble through the portal, so both parts stop
  // the Subject cell's click from also opening the alert preview.
  return (
    <Popover>
      <PopoverTrigger
        className="text-muted-foreground block max-w-64 cursor-pointer truncate text-left text-xs hover:underline"
        onClick={(event) => {
          event.stopPropagation();
        }}>
        {label} → {recipients.slice(0, VISIBLE_RECIPIENTS).join(', ')}
        {hidden > 0 ? ` +${String(hidden)} more` : ''}
      </PopoverTrigger>
      <PopoverContent
        className="w-80"
        onClick={(event) => {
          event.stopPropagation();
        }}>
        <PopoverHeader>
          <PopoverTitle>
            {label} ({recipients.length})
          </PopoverTitle>
        </PopoverHeader>
        <ul className="max-h-60 overflow-y-auto text-xs select-text">
          {recipients.map((recipient) => (
            <li key={recipient} className="break-all">
              {recipient}
            </li>
          ))}
        </ul>
        <CopyButton
          type="button"
          size="sm"
          variant="outline"
          label="Copy all"
          value={() => recipients.join(', ')}
        />
      </PopoverContent>
    </Popover>
  );
}

const COLUMN_COUNT = 8;

const VISIBLE_CONTENT_IDS = 2;

// An alert can mix channels and videos, so the row counts each kind and lists the first few IDs
// rather than picking one submission to represent the rest; the detail sheet shows every one.
function AlertContentCell({alert}: {readonly alert: P0Alert}) {
  const channels = alert.submissions.filter((s) => s.submission_type === 'Actor').length;
  const videos = alert.submissions.length - channels;
  const ids = alert.submissions.map((s) => s.item_external_id ?? s.content_id);
  const hidden = ids.length - VISIBLE_CONTENT_IDS;
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex flex-wrap gap-1">
        {channels > 0 ? (
          <Badge variant="secondary">
            {channels}{' '}
            {channels === 1 ? ACTOR_LABEL.toLowerCase() : `${ACTOR_LABEL.toLowerCase()}s`}
          </Badge>
        ) : null}
        {videos > 0 ? (
          <Badge variant="secondary">
            {videos}{' '}
            {videos === 1 ? CONTENT_LABEL.toLowerCase() : `${CONTENT_LABEL.toLowerCase()}s`}
          </Badge>
        ) : null}
      </div>
      <TruncatedText className="text-muted-foreground max-w-full font-mono text-xs">
        {ids.slice(0, VISIBLE_CONTENT_IDS).join(', ')}
        {hidden > 0 ? ` +${String(hidden)} more` : ''}
      </TruncatedText>
    </div>
  );
}

// What the platform has done across every submission, counted: a mixed alert has no single status.
function AlertEnforcementCell({alert}: {readonly alert: P0Alert}) {
  const counts = new Map<string, number>();
  for (const sub of alert.submissions) {
    for (const action of enforcementActions(sub)) {
      counts.set(action, (counts.get(action) ?? 0) + 1);
    }
  }

  if (counts.size === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {[...counts].map(([action, count]) => (
        <Badge key={action} variant="destructive">
          {action}
          {alert.submissions.length > 1 ? ` ×${String(count)}` : ''}
        </Badge>
      ))}
    </div>
  );
}

function AlertPolicyCell({alert}: {readonly alert: P0Alert}) {
  const policyViolations = [...new Set(alert.submissions.map((s) => s.policy_violation))];
  const risks = [...new Set(alert.submissions.flatMap((s) => s.risks))];
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex flex-wrap gap-1">
        {(policyViolations.length > 0 ? policyViolations : [alert.policy_violation]).map((pv) => (
          <Badge key={pv} variant="outline">
            {pv}
          </Badge>
        ))}
      </div>
      <TruncatedText className="text-muted-foreground max-w-full text-xs">
        {risks.join(', ') || '—'}
      </TruncatedText>
    </div>
  );
}

function downloadAlertsCsv(alerts: readonly P0Alert[]) {
  const csv = Papa.unparse(
    alerts.map((a) => ({
      subject: a.subject ?? '',
      status: statusBadge(a.status).label,
      policy_violation: [...new Set(a.submissions.map((s) => s.policy_violation))].join('; '),
      content_ids: a.submissions.map((s) => s.item_external_id ?? s.content_id).join('; '),
      created_by: a.created_by,
      created_at: a.created_at,
      decided_by: a.decided_by ?? '',
      decision_reason: a.decision_reason ?? ''
    }))
  );
  const url = URL.createObjectURL(new Blob([csv], {type: 'text/csv'}));
  const link = document.createElement('a');
  link.href = url;
  link.download = `p0-alerts-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function P0AlertsWorkspace({
  canDecide,
  focusAlertId
}: {
  readonly canDecide: boolean;
  readonly focusAlertId: string | undefined;
}) {
  const navigate = useNavigate();
  // The status tab lives in the URL, so a reload or a shared link lands on the same list.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusTab =
    STATUS_TABS.find((tab) => tab.value === searchParams.get('status'))?.value ?? 'all';
  const [rejectTarget, setRejectTarget] = useState<P0Alert | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewTarget, setPreviewTarget] = useState<P0Alert | null>(null);

  const [query, setQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [policyViolation, setPolicyViolation] = useState('');
  const debouncedQuery = useDebounce(query.trim(), 300);
  const {data: stats} = useSubmissionStats(true);

  const {
    data,
    isLoading,
    isFetching,
    isFetchNextPageError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteP0Alerts({
    ...(statusTab === 'all' ? {} : {status: statusTab}),
    ...(debouncedQuery ? {q: debouncedQuery} : {}),
    ...(policyViolation ? {policy_violation: policyViolation} : {}),
    ...(dateFrom ? {date_from: dateFrom} : {}),
    ...(dateTo ? {date_to: dateTo} : {})
  });

  // Deep link from a Slack "Review Now"/"View Case" button (/p0-alerts/:id) - fetched independently
  // of the (paginated, status-filtered) list above so it opens the preview even when the alert
  // wouldn't otherwise be on the current page/tab.
  const {data: focusedAlert} = useP0Alert(focusAlertId ?? '', Boolean(focusAlertId));
  useEffect(() => {
    if (focusedAlert) {
      setPreviewTarget(focusedAlert);
    }
  }, [focusedAlert]);

  function closePreview() {
    setPreviewTarget(null);
    if (focusAlertId) {
      void navigate('/p0-alerts', {replace: true});
    }
  }

  const decide = useDecideP0Alert();
  const amend = useAmendP0Alert();
  // The decide mutation's isPending is shared by every row (one mutation instance) - tracking which
  // alert is actually being acted on lets an in-flight Approve/Retry on one row leave every other
  // row's buttons clickable instead of freezing the whole table.
  const [actingId, setActingId] = useState<string | null>(null);

  const alerts = data?.pages.flatMap((page) => page.results) ?? [];
  const totalCount = data?.pages[0]?.total_count ?? 0;
  // The open sheet follows the list, so an approve or amend shows its new state without a reopen.
  const detailAlert = previewTarget
    ? (alerts.find((a) => a.id === previewTarget.id) ?? previewTarget)
    : null;

  // The sentinel is a row inside the scrolling table, so it only comes into view when the analyst
  // reaches the bottom. A failed page stops it firing until a filter change or refresh.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage || isFetchNextPageError) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        void fetchNextPage();
      }
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  async function handleApprove(alert: P0Alert): Promise<boolean> {
    setError(null);
    setActingId(alert.id);
    try {
      await decide.mutateAsync({id: alert.id, action: 'approve'});
      return true;
    } catch (approveError) {
      // A toast rather than the page banner: this also runs from the preview dialog, which covers it.
      showToast(getApiErrorMessage(approveError, 'Failed to approve P0 alert.'), 'error');
      return false;
    } finally {
      setActingId(null);
    }
  }

  async function handleRetryDelivery(alert: P0Alert) {
    setError(null);
    setActingId(alert.id);
    try {
      await decide.mutateAsync({id: alert.id, action: 'retry-delivery'});
      showToast('Delivery retry queued.');
    } catch (retryError) {
      setError(getApiErrorMessage(retryError, 'Failed to retry P0 alert delivery.'));
    } finally {
      setActingId(null);
    }
  }

  function openReject(alert: P0Alert) {
    setError(null);
    setRejectReason('');
    setRejectTarget(alert);
  }

  // Also the Cancel path - a deep-linked reviewer backing out of Reject should return to the plain
  // list URL rather than being left on /p0-alerts/:id with no dialog open.
  function closeReject() {
    setRejectTarget(null);
    if (focusAlertId) {
      void navigate('/p0-alerts', {replace: true});
    }
  }

  async function handleReject() {
    if (!rejectTarget) {
      return;
    }

    setError(null);
    try {
      await decide.mutateAsync({id: rejectTarget.id, action: 'reject', reason: rejectReason});
      closeReject();
    } catch (rejectError) {
      showToast(getApiErrorMessage(rejectError, 'Failed to reject P0 alert.'), 'error');
    }
  }

  const [amendTarget, setAmendTarget] = useState<P0Alert | null>(null);
  const [amendStep, setAmendStep] = useState<'edit' | 'confirm'>('edit');
  const [amendSubject, setAmendSubject] = useState('');
  const [amendNotes, setAmendNotes] = useState('');
  const [amendVariants, setAmendVariants] = useState<Set<string>>(new Set());
  const [amendReason, setAmendReason] = useState('');

  function openAmend(alert: P0Alert) {
    setError(null);
    setAmendTarget(alert);
    setAmendStep('edit');
    setAmendSubject(alert.subject ?? '');
    setAmendNotes(alert.notes ?? '');
    setAmendVariants(new Set(alert.escalation_variants));
    setAmendReason('');
  }

  // Also the Cancel path - a deep-linked reviewer backing out of Amend should return to the plain
  // list URL rather than being left on /p0-alerts/:id with no dialog open.
  function closeAmend() {
    setAmendTarget(null);
    if (focusAlertId) {
      void navigate('/p0-alerts', {replace: true});
    }
  }

  const amendPolicyViolations = amendTarget
    ? [...new Set(amendTarget.submissions.map((s) => s.policy_violation))]
    : [];
  const {data: amendAllVariants = []} = useEscalationVariants();
  const missingAmendVariantVerticals = getPolicyViolationsMissingVariant(
    amendPolicyViolations,
    amendAllVariants,
    amendVariants
  );

  const amendDiff = amendTarget
    ? [
        {
          field: 'Subject',
          before: amendTarget.subject ?? '(none)',
          after: amendSubject.trim() || '(none)',
          changed: (amendTarget.subject ?? '') !== amendSubject.trim()
        },
        {
          field: 'Email Body',
          before: amendTarget.notes_text || '(none)',
          after: isRichTextEmpty(amendNotes) ? '(none)' : richTextToPlainLines(amendNotes),
          changed: (amendTarget.notes ?? '') !== amendNotes
        },
        {
          field: 'Tags',
          before: amendTarget.escalation_variants.join(', ') || '(none)',
          after: [...amendVariants].join(', ') || '(none)',
          changed:
            JSON.stringify([...amendTarget.escalation_variants].sort()) !==
            JSON.stringify([...amendVariants].sort())
        }
      ].filter((d) => d.changed)
    : [];

  async function handleAmend() {
    if (!amendTarget) {
      return;
    }

    setError(null);
    try {
      await amend.mutateAsync({
        id: amendTarget.id,
        body: {
          subject: amendSubject.trim(),
          notes: isRichTextEmpty(amendNotes) ? '' : amendNotes,
          escalation_variants: [...amendVariants],
          reason: amendReason.trim()
        }
      });
      closeAmend();
    } catch (amendError) {
      showToast(getApiErrorMessage(amendError, 'Failed to amend P0 alert.'), 'error');
    }
  }

  const policyViolations = stats?.by_policy_violation.map((v) => v.policy_violation) ?? [];

  return (
    // The filter bar is a white strip over the page's grey and the table panel below it scrolls on
    // its own, as on Submissions, QC Review and Weekly QA.
    <div className="text-foreground flex h-full w-full flex-col">
      <PageHeaderActions>
        <Button
          variant="outline"
          size="icon-sm"
          disabled={isFetching}
          aria-label="Refresh"
          onClick={() => void refetch()}>
          {isFetching ? <Spinner /> : <RotateCw />}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={alerts.length === 0}
          onClick={() => {
            downloadAlertsCsv(alerts);
          }}>
          <Download />
          Export current as CSV ({alerts.length.toLocaleString()})
        </Button>
      </PageHeaderActions>

      <div className="bg-card border-border shrink-0 space-y-3 border-b px-5 py-3">
        <Tabs
          value={statusTab}
          onValueChange={(value) => {
            const next = new URLSearchParams(searchParams);
            if (value === 'all') {
              next.delete('status');
            } else {
              next.set('status', value);
            }

            setSearchParams(next, {replace: true});
          }}>
          <TabsList>
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center gap-2">
          <InputGroup className="w-56 max-w-full">
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search P0 alerts"
              value={query}
              placeholder="Search subject, content ID, handle…"
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
          </InputGroup>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <FilterChip
                  label={policyViolation || 'Policy violation'}
                  active={policyViolation !== ''}
                />
              }
            />
            <DropdownMenuContent align="start">
              <DropdownMenuRadioGroup value={policyViolation}>
                <DropdownMenuRadioItem
                  value=""
                  onClick={() => {
                    setPolicyViolation('');
                  }}>
                  All policy violations
                </DropdownMenuRadioItem>
                {policyViolations.map((pv) => (
                  <DropdownMenuRadioItem
                    key={pv}
                    value={pv}
                    onClick={() => {
                      setPolicyViolation(pv);
                    }}>
                    {pv}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />

          <span className="text-muted-foreground text-xs">
            {totalCount.toLocaleString()} alert{totalCount === 1 ? '' : 's'}
          </span>

          <div className="ml-auto">
            <SensitiveMediaToggle />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 px-5 py-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <TablePanel className="flex min-h-0 flex-col">
          <Table containerClassName="min-h-0 overflow-y-auto [&_thead_th]:bg-card [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:shadow-[inset_0_-1px_0_var(--border)]">
            <TableHeader>
              <TableRow>
                <TableHead>Alert</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Platform Action</TableHead>
                <TableHead>Policy / Risks</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Reviewer</TableHead>
                <TableHead className="w-6">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                      <Spinner />
                      Loading…
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}

              {!isLoading && alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="p-0">
                    <Empty className="py-10">
                      <EmptyHeader>
                        <EmptyMedia variant="icon">
                          <OctagonAlert />
                        </EmptyMedia>
                        <EmptyTitle>No P0 alerts</EmptyTitle>
                        <EmptyDescription>
                          {statusTab === 'all' && !debouncedQuery && !policyViolation && !dateFrom
                            ? 'No P0 alert has been raised yet.'
                            : 'No P0 alert matches these filters.'}
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  </TableCell>
                </TableRow>
              ) : null}

              {alerts.map((alert) => (
                <TableRow
                  key={alert.id}
                  className={cn('cursor-pointer', detailAlert?.id === alert.id && 'bg-muted/50')}
                  onClick={() => {
                    setPreviewTarget(alert);
                  }}>
                  <TableCell className="max-w-[280px]">
                    <div className="flex flex-col gap-0.5">
                      {/* The row's click is a pointer convenience; the link is the keyboard route
                          to the detail, and it stops the row opening it a second time. */}
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="text-foreground h-auto w-fit max-w-full justify-start p-0 text-xs font-medium"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPreviewTarget(alert);
                        }}>
                        <TruncatedText>{alert.subject ?? 'Untitled P0 Alert'}</TruncatedText>
                      </Button>
                      {/* What was actually sent, recorded at delivery. Undelivered alerts show a
                          live "would notify" preview to reviewers instead - never for a delivered
                          one, where re-resolving now drifts from what went out. */}
                      <div
                        role="presentation"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}>
                        {canDecide && alert.sent_recipients.length > 0 ? (
                          <RecipientSummary label="Sent" recipients={alert.sent_recipients} />
                        ) : canDecide && alert.debug_recipients.length > 0 ? (
                          <RecipientSummary
                            label="Would notify now"
                            recipients={alert.debug_recipients}
                          />
                        ) : null}
                      </div>
                      {alert.notes_text ? (
                        <TruncatedText className="text-muted-foreground max-w-64 text-xs italic">
                          {alert.notes_text.replaceAll('\n', ' ')}
                        </TruncatedText>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[380px]">
                    <AlertContentCell alert={alert} />
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusBadge(alert.status).variant}>
                      {statusBadge(alert.status).label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <AlertEnforcementCell alert={alert} />
                  </TableCell>
                  <TableCell className="max-w-[200px]">
                    <AlertPolicyCell alert={alert} />
                  </TableCell>
                  <TableCell
                    className="text-muted-foreground whitespace-nowrap"
                    title={format(new Date(alert.created_at), 'PPpp')}>
                    <div>{alert.created_by.split('@')[0] ?? alert.created_by}</div>
                    <div className="text-xs">
                      {formatDistanceToNow(new Date(alert.created_at), {addSuffix: true})}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {alert.decided_by ? (
                      <div className="flex flex-col">
                        <span>{alert.decided_by}</span>
                        {alert.decision_reason ? (
                          <span className="text-destructive-ink">{alert.decision_reason}</span>
                        ) : null}
                        {alert.delivery_error ? (
                          <TruncatedText className="text-destructive-ink max-w-64">
                            {alert.delivery_error}
                          </TruncatedText>
                        ) : null}
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell
                    onClick={(event) => {
                      event.stopPropagation();
                    }}>
                    {canDecide &&
                    (alert.status === 'pending_approval' || alert.status === 'approved') ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          aria-label="P0 alert actions"
                          disabled={actingId === alert.id}
                          render={
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-muted-foreground"
                            />
                          }>
                          {actingId === alert.id ? <Spinner /> : <MoreHorizontal />}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {alert.status === 'pending_approval' ? (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  void handleApprove(alert);
                                }}>
                                <Check />
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  openAmend(alert);
                                }}>
                                <Pencil />
                                Amend
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                variant="destructive"
                                onClick={() => {
                                  openReject(alert);
                                }}>
                                <X />
                                Reject
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => {
                                void handleRetryDelivery(alert);
                              }}>
                              <RotateCw />
                              Retry Delivery
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}

              {hasNextPage || isFetchNextPageError ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMN_COUNT} className="p-0">
                    {isFetchNextPageError ? (
                      <p className="text-destructive-ink py-4 text-center text-xs">
                        Couldn’t load more alerts. Change a filter or refresh to try again.
                      </p>
                    ) : (
                      <div ref={sentinelRef} className="flex justify-center py-4">
                        {isFetchingNextPage ? <Spinner /> : null}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TablePanel>
      </div>

      <Dialog
        open={rejectTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeReject();
          }
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject P0 Alert</DialogTitle>
            <DialogDescription>
              A reason is required so the analyst who raised this alert understands the decision.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <Field>
              <FieldLabel htmlFor="reject-reason">Reason for rejection</FieldLabel>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                placeholder="Reason for rejection"
                rows={3}
                onChange={(event) => {
                  setRejectReason(event.target.value);
                }}
              />
            </Field>
          </DialogBody>

          <DialogFooter sticky>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                closeReject();
              }}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={decide.isPending || rejectReason.trim().length === 0}
              onClick={() => void handleReject()}>
              {decide.isPending ? <Spinner /> : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={amendTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeAmend();
          }
        }}>
        <DialogContent className="sm:max-w-3xl">
          {amendTarget && amendStep === 'edit' ? (
            <>
              <DialogHeader>
                <DialogTitle>Amend P0 Alert</DialogTitle>
                <DialogDescription>
                  Fix up the alert before approving it - it will be delivered immediately once
                  confirmed.
                </DialogDescription>
              </DialogHeader>

              {/* The body is the one part that scrolls between the fixed title and the pinned
                  actions, so Review Changes stays reachable however long the form runs. */}
              <DialogBody>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-4">
                    <Field>
                      <FieldLabel htmlFor="amend-subject">
                        Subject line <span className="text-destructive-ink">*</span>
                      </FieldLabel>
                      <Input
                        id="amend-subject"
                        value={amendSubject}
                        onChange={(event) => {
                          setAmendSubject(event.target.value);
                        }}
                      />
                    </Field>
                    <EscalationVariantPicker
                      policyViolations={amendPolicyViolations}
                      value={amendVariants}
                      onChange={setAmendVariants}
                    />
                    <Field>
                      <FieldLabel>Email body</FieldLabel>
                      <RichTextArea value={amendNotes} onChange={setAmendNotes} />
                    </Field>
                  </div>

                  <EmailPreview subject={amendSubject} bodyHtml={amendNotes} />
                </div>
              </DialogBody>

              <DialogFooter sticky>
                <Button type="button" variant="ghost" onClick={closeAmend}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={amendSubject.trim() === '' || missingAmendVariantVerticals.length > 0}
                  onClick={() => {
                    setAmendStep('confirm');
                  }}>
                  Review Changes
                </Button>
              </DialogFooter>
            </>
          ) : null}

          {amendTarget && amendStep === 'confirm' ? (
            <>
              <DialogHeader>
                <DialogTitle>Confirm Amendment</DialogTitle>
                <DialogDescription>
                  {amendDiff.length === 0
                    ? 'No fields were changed.'
                    : 'Review what changed, then say why before delivering.'}
                </DialogDescription>
              </DialogHeader>

              <DialogBody>
                {amendDiff.length > 0 ? (
                  <div className="divide-border border-border divide-y rounded-lg border">
                    {amendDiff.map((d) => (
                      <div key={d.field} className="space-y-1 px-3 py-2">
                        <p className="text-foreground text-xs font-medium">{d.field}</p>
                        {/* Removed and added lines are told apart by an icon and the strike,
                            not by inking the text: the success surface is not a text colour and
                            fails contrast as one. */}
                        <p className="text-destructive-ink flex items-start gap-1.5 text-xs whitespace-pre-wrap line-through">
                          <StatusIcon tone="destructive" className="mt-0.5" />
                          {d.before}
                        </p>
                        <p className="text-foreground flex items-start gap-1.5 text-xs font-medium whitespace-pre-wrap">
                          <StatusIcon tone="success" className="mt-0.5" />
                          {d.after}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : null}

                <Field>
                  <FieldLabel htmlFor="amend-reason">Reason for amending</FieldLabel>
                  <Textarea
                    id="amend-reason"
                    value={amendReason}
                    placeholder="Why these changes were needed"
                    rows={3}
                    onChange={(event) => {
                      setAmendReason(event.target.value);
                    }}
                  />
                </Field>
              </DialogBody>

              <DialogFooter sticky>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setAmendStep('edit');
                  }}>
                  Back
                </Button>
                <Button
                  type="button"
                  disabled={amend.isPending || amendReason.trim().length === 0}
                  onClick={() => void handleAmend()}>
                  {amend.isPending ? <Spinner /> : null}
                  Confirm & Deliver
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <P0AlertDetailSheet
        alert={detailAlert}
        canDecide={canDecide}
        acting={actingId === detailAlert?.id}
        onOpenChange={(open) => {
          if (!open) {
            closePreview();
          }
        }}
        onApprove={(alert) => {
          void (async () => {
            if (await handleApprove(alert)) {
              closePreview();
            }
          })();
        }}
        onAmend={(alert) => {
          openAmend(alert);
          setPreviewTarget(null);
        }}
        onReject={(alert) => {
          openReject(alert);
          setPreviewTarget(null);
        }}
        onRetry={(alert) => void handleRetryDelivery(alert)}
      />
    </div>
  );
}
